import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Search } from "lucide-react";
import { Button } from "../ui/button";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../../contexts/AuthContext";
import { usePageTitle } from "../../contexts/PageTitleContext";
import { roleLabel } from "../../lib/utils";

/**
 * Substitui Topbar.tsx. Busca global cross-canal — o UNICO lugar do app com
 * campo de busca (comportamento §9 do handoff); a tela de resultado mostra
 * o termo como chip, nunca um segundo campo. Sem hamburger de menu: no
 * mobile a navegacao vive na TabBar, o menu lateral nao aparece mais ali.
 *
 * Desktop (>=64rem/lg) e mobile (<64rem) sao DOIS headers, nao um so
 * espremido: o mobile e a barra "moldura do app" do handoff (coral, voltar +
 * titulo da tela, tema + avisos), sem o campo de busca (que so cabe fixo no
 * desktop — no mobile a busca vive na aba "Buscar" da TabBar). Achado do
 * smoke comparativo 2026-09-22: header unico fazia o campo de busca
 * espremer contra sino+logout a 390px, sem cor nem titulo de tela, e sumia
 * ao rolar por nao ser moldura de verdade.
 */
export function Header(): JSX.Element {
  return (
    <>
      <div className="hidden lg:block">
        <DesktopHeader />
      </div>
      <div className="lg:hidden">
        <MobileHeader />
      </div>
    </>
  );
}

function DesktopHeader(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = (e: FormEvent): void => {
    e.preventDefault();
    const term = q.trim();
    if (term.length === 0) return;
    navigate(`/buscar?q=${encodeURIComponent(term)}`);
    setQ("");
  };

  // Atalho ⌘K/Ctrl+K foca a busca global — a pilula no campo so e credivel
  // se a tecla realmente funcionar (handoff mostra "⌘K" dentro do campo).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="header">
      <form onSubmit={handleSearch} className="header__search grow">
        <Search className="icon" aria-hidden style={{ color: "var(--color-ink-3)" }} />
        <label className="sr-only" htmlFor="header-search">
          Busca global cross-canal
        </label>
        <input
          id="header-search"
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar SKU, nome, tag…"
          autoComplete="off"
        />
        <span className="kbd" aria-hidden>⌘K</span>
      </form>

      {/* header__actions: margin-left:auto encosta este bloco na borda
          direita, sempre — independe do quanto a busca cresceu (fix
          22/09, print circulado: bloco ficava "no meio" com vazio a
          direita). Fidelidade 3 (23/09/2026, F1): sem ThemeToggle aqui —
          o protótipo não tem ícone de tema no header desktop, ele mora só
          no rodapé do menu (Nav.tsx já tem). Bloco do usuário é nome
          negrito + papel em texto pequeno cinza, SEM pílula (era .chip). */}
      <div className="cluster header__actions">
        <NotificationBell />

        <div className="cluster" style={{ paddingLeft: ".75rem", borderLeft: ".0625rem solid var(--color-line)", marginLeft: ".25rem" }}>
          <div style={{ textAlign: "right", lineHeight: 1.2 }} className="hidden md:block">
            <div className="row__title" style={{ fontSize: "var(--text-base)" }}>{user?.name ?? "—"}</div>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--color-ink-2)" }}>
              {user?.role_name ?? roleLabel(user?.role)}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => void logout()} aria-label="Sair" title="Sair">
            <LogOut className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>
    </header>
  );
}

/**
 * Barra fixa coral do mobile (handoff: "moldura do app", fora da area com
 * padding). Voltar via history — sumida so na raiz "/" (Painel), que e onde
 * a TabBar ja cobre a navegacao pra tras. Titulo vem de PageHeader via
 * PageTitleContext, nao de tabela de rotas (ela envelheceria a cada rota
 * nova; o titulo publicado e a mesma string que a propria tela ja declara).
 */
function MobileHeader(): JSX.Element {
  const { title } = usePageTitle();
  const location = useLocation();
  const navigate = useNavigate();
  const isRoot = location.pathname === "/";

  return (
    <header
      className="cluster"
      style={{
        minHeight: "var(--header-h)",
        flexShrink: 0,
        padding: "0 .5rem",
        background: "var(--color-nav-bg)",
        color: "#fff",
      }}
    >
      {/* Titulo cresce e trunca (grow+truncate) em vez de fixo — ficha de
       * produto manda o NOME (pode ser longo, ex: "PARKA CORE ACOLCHOADA"),
       * nao so o SKU curto que nunca estourava. Sem isto o nome empurrava
       * tema/sino pra fora da barra em vez de reticenciar. */}
      {isRoot ? (
        <span className="row__title grow truncate" style={{ color: "#fff", fontSize: "var(--text-lg)" }}>{title}</span>
      ) : (
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="cluster grow"
          aria-label="Voltar"
          style={{ background: "transparent", border: "none", color: "#fff", minHeight: "var(--tap-mobile)", padding: "0 .25rem", minWidth: 0 }}
        >
          <ArrowLeft className="icon" aria-hidden style={{ flexShrink: 0 }} />
          <span className="row__title truncate" style={{ color: "#fff", fontSize: "var(--text-lg)" }}>{title}</span>
        </button>
      )}
      <div className="cluster mobile-header-actions" style={{ gap: ".125rem", flexShrink: 0 }}>
        <ThemeToggle />
        <NotificationBell />
      </div>
    </header>
  );
}
